import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import IncomingInvoiceFlowDialog from "@/components/IncomingInvoiceFlowDialog";
import QuickImportDialog from "@/components/QuickImportDialog";
import QuickImportWizard from "@/components/QuickImportWizard";
import { buildFinanceShell } from "@/components/quickImport/financeShell";
import { useNavVisibility } from "@/components/quickImport/useNavVisibility";
import { useTopbarData } from "@/components/quickImport/useTopbarData";
import { withPanelBase } from "@/lib/authz";
// Finans kiti (JS + CSS + font) yalnız bayrağı açık kullanıcılar için yüklenir.
const QuickImportWorkspace = lazy(() => import("@/components/QuickImportWorkspace"));
// ObiHub OS liste ekranı: yalnız çalışma alanı bayrağıyla yüklenir; bayraksız kullanıcı kit/font indirmez.
const IncomingInvoicesOS = lazy(() => import("@/components/incoming/IncomingInvoicesOS"));
import { toast } from "sonner";
import { FileBadge2, Loader2, Search, ChevronLeft, ChevronRight, Eye, Download, CheckCircle2, Clock3, CircleAlert, TrendingUp, PackagePlus, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// v2: anahtar kullanıcı(tenant) + sağlayıcı kapsamlıdır; v1 (yalnız sağlayıcı) kayıtları okunmaz.
const CACHE_KEY_PREFIX = "incoming_e_invoices_cache_v2";

function buildCacheKey(userId, provider) {
  const uid = String(userId || "").trim();
  const prov = String(provider || "").trim();
  if (!uid || !prov) return null; // kapsam belirsizse önbellek kullanılmaz
  return `${CACHE_KEY_PREFIX}:${uid}:${prov}`;
}
const CACHE_TTL_MS = 10 * 60 * 1000;
const PAGE_FETCH_DELAY_MS = 180;
const RETRY_BASE_DELAY_MS = 600;

const PROVIDER_LABELS = {
  "parasut": "Paraşüt",
  "bizimhesap": "BizimHesap",
  "benimpos": "BenimPOS",
  "turkcell-efatura": "Turkcell e-Fatura",
};

function isInPeriod(dateStr, period) {
  if (!dateStr || period === "all") return true;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return false;
  const now = Date.now();
  const diffMs = now - d.getTime();
  const days = period === "7d" ? 7 : 30;
  return diffMs >= 0 && diffMs <= days * 24 * 60 * 60 * 1000;
}

function getConnectedProviders(cfg, turkcellConnected) {
  const list = [];
  if (Boolean(cfg?.parasut?.connected)) list.push("parasut");
  if (Boolean(cfg?.bizimhesap?.connected)) list.push("bizimhesap");
  if (Boolean(cfg?.benimpos?.connected)) list.push("benimpos");
  if (turkcellConnected) list.push("turkcell-efatura");
  return list;
}

function pickDefaultProvider(providers, preferred) {
  if (!providers.length) return "";
  if (preferred && providers.includes(preferred)) return preferred;
  return providers[0];
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("tr-TR");
}

function formatTryRight(value) {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return "-";
  return `${amount.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`;
}

function mapIncomingStatus(status) {
  const raw = String(status || "").trim().toLowerCase();
  if (!raw) return "-";
  if (raw.includes("cancel") || raw.includes("iptal") || raw.includes("reject") || raw.includes("red")) return "İptal";
  if (raw.includes("success") || raw.includes("accept") || raw.includes("approv") || raw.includes("onay")) return "Resmileşti";
  if (raw.includes("receive") || raw.includes("alındı") || raw.includes("alin")) return "Alındı";
  if (raw.includes("wait") || raw.includes("pending") || raw.includes("bek") || raw.includes("draft") || raw.includes("taslak")) {
    return "Taslak Gönderildi";
  }
  return "Taslak Gönderildi";
}

function statusBadge(status) {
  const label = mapIncomingStatus(status);
  const normalized = String(label || "").toLowerCase();
  const isAccepted = normalized.includes("kabul") || normalized.includes("resmileş");
  const isWaiting = normalized.includes("bek");
  const isRejected = normalized.includes("redd") || normalized.includes("iptal");
  const cls = isAccepted
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : isWaiting
      ? "bg-amber-50 text-amber-700 border-amber-200"
      : isRejected
        ? "bg-rose-50 text-rose-700 border-rose-200"
        : "bg-muted/60 text-foreground/80 border-border";
  const dot = isAccepted ? "bg-emerald-500" : isWaiting ? "bg-amber-500" : isRejected ? "bg-rose-500" : "bg-muted-foreground";
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap px-2 py-0.5 rounded-md text-xs leading-none border ${cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  );
}

function mapInvoiceTypeLabel(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "-";
  if (raw === "temelfatura") return "Temel";
  if (raw === "ticarifatura") return "Ticari";
  if (raw === "commercial" || raw.includes("ticari")) return "Ticari";
  if (raw === "basic" || raw.includes("temel")) return "Temel";
  return value;
}

export default function IncomingEInvoicesPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const cacheUserId = user?.id || user?._id || "";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notConnected, setNotConnected] = useState(false);
  const [accountingProvider, setAccountingProvider] = useState("");
  const [providerUnsupported, setProviderUnsupported] = useState(false);
  const [sourceRequired, setSourceRequired] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTitle, setPreviewTitle] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [allInvoices, setAllInvoices] = useState([]);
  const [showingCachedData, setShowingCachedData] = useState(false);
  const [period, setPeriod] = useState("30d");
  const [stockInvoice, setStockInvoice] = useState(null);
  // Yeni akış paneli: sunucu bayrağı kapalıysa (varsayılan) hiç görünmez, eski akış aynen kalır.
  const [flowInfo, setFlowInfo] = useState(null);
  const flowEnabled = flowInfo?.enabled === true;
  const [flowInvoice, setFlowInvoice] = useState(null);
  const [quickImportCapability, setQuickImportCapability] = useState(null);
  const quickImportEnabled = quickImportCapability?.enabled === true && quickImportCapability?.preview === true;
  const [quickInvoice, setQuickInvoice] = useState(null);
  const osList = quickImportEnabled && quickImportCapability?.wizard === true && quickImportCapability?.workspace === true;
  const navVisibility = useNavVisibility(Boolean(quickInvoice) || osList);
  const topbar = useTopbarData(osList, user, (p) => withPanelBase(p, user, location.pathname));
  const [stockLines, setStockLines] = useState([{ name: "", sku: "", quantity: 1, unit_cost: 0 }]);
  const [stockSaving, setStockSaving] = useState(false);
  const deepLinkHandledRef = useRef("");
  const perPage = 15;

  const applyPage = useCallback((source, q, currentPage) => {
    const query = String(q || "").trim().toLowerCase();
    const filtered = query
      ? source.filter((x) =>
          [x.invoice_no, x.sender_name, x.uuid, x.status, x.scenario, x.profile_id, x.invoice_type]
            .map((v) => String(v || "").toLowerCase())
            .some((v) => v.includes(query))
        )
      : source;
    const start = (currentPage - 1) * perPage;
    const end = start + perPage;
    setItems(filtered.slice(start, end));
    setTotal(filtered.length);
  }, []);

  useEffect(() => {
    // Kullanıcı değişince başka kullanıcıların (ve eski v1) önbellek kayıtlarını temizle.
    try {
      const keep = `${CACHE_KEY_PREFIX}:${String(cacheUserId || "")}:`;
      const drop = [];
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const k = sessionStorage.key(i) || "";
        if (k.startsWith("incoming_e_invoices_cache") && !(cacheUserId && k.startsWith(keep))) drop.push(k);
      }
      drop.forEach((k) => sessionStorage.removeItem(k));
    } catch {
      // ignore storage errors
    }
  }, [cacheUserId]);

  const readCache = useCallback((provider) => {
    try {
      const key = buildCacheKey(cacheUserId, provider);
      if (!key) return null;
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.ts || !Array.isArray(parsed?.rows)) return null;
      if (Date.now() - Number(parsed.ts) > CACHE_TTL_MS) return null;
      return parsed.rows;
    } catch {
      return null;
    }
  }, [cacheUserId]);

  const writeCache = useCallback((provider, rows) => {
    try {
      const key = buildCacheKey(cacheUserId, provider);
      if (!key) return;
      sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), rows }));
    } catch {
      // ignore storage errors
    }
  }, [cacheUserId]);

  const fetchAllInvoices = useCallback(async (provider) => {
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const requestWithRetry = async (url, maxRetries = 3) => {
      let attempt = 0;
      while (attempt <= maxRetries) {
        try {
          return await api.get(url);
        } catch (err) {
          const message = String(err?.message || "");
          const is429 = message.includes("429");
          if (!is429 || attempt === maxRetries) throw err;
          await sleep(RETRY_BASE_DELAY_MS * (attempt + 1));
          attempt += 1;
        }
      }
      return null;
    };

    const widePerPage = 100;
    const base = provider === "bizimhesap" ? "/bizimhesap/incoming-e-invoices" : "/parasut/incoming-e-invoices";
    const first = await requestWithRetry(`${base}?page=1&per_page=${widePerPage}`);
    const firstItems = first?.invoices || [];
    const firstTotal = Number(first?.total || firstItems.length || 0);
    const totalPages = Math.max(1, Math.ceil(firstTotal / widePerPage));
    const all = [...firstItems];
    for (let p = 2; p <= totalPages; p += 1) {
      await sleep(PAGE_FETCH_DELAY_MS);
      const next = await requestWithRetry(`${base}?page=${p}&per_page=${widePerPage}`);
      all.push(...(next?.invoices || []));
    }
    return all;
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [cfg, turkcellStatus, sourceRes] = await Promise.all([
        api.get("/settings/accounting").catch(() => null),
        api.get("/turkcell-efatura/status").catch(() => null),
        api.get("/settings/incoming-einvoice-source").catch(() => null),
      ]);
      const turkcellConnected = Boolean(turkcellStatus?.connected);
      const providers = getConnectedProviders(cfg, turkcellConnected);

      if (!providers.length) {
        setNotConnected(true);
        setProviderUnsupported(false);
        setAccountingProvider("");
        setItems([]);
        setTotal(0);
        return;
      }

      const configuredSource = String(sourceRes?.source || "").trim();
      // Tek doğruluk kaynağı DB'deki Firmalarım seçimidir. Birden fazla bağlı
      // uygulama varken ekran içinde ikinci bir sağlayıcı seçimi yapılmaz.
      if (providers.length > 1 && (!configuredSource || !providers.includes(configuredSource))) {
        setSourceRequired(true);
        setAccountingProvider("");
        setItems([]);
        setTotal(0);
        return;
      }
      setSourceRequired(false);
      const provider = configuredSource && providers.includes(configuredSource)
        ? configuredSource
        : pickDefaultProvider(providers, String(cfg?.provider || "").toLowerCase());

      setNotConnected(false);
      setAccountingProvider(provider);
      setShowingCachedData(false);

      if (provider === "bizimhesap") {
        setProviderUnsupported(true);
        setItems([]);
        setTotal(0);
        return;
      }
      setProviderUnsupported(false);

      if (provider === "benimpos") {
        const res = await api.get(`/benimpos/incoming-e-invoices?page=${page}&per_page=${perPage}`);
        const rows = Array.isArray(res?.items) ? res.items : [];
        setItems(rows);
        setTotal(Number(res?.total || rows.length || 0));
        return;
      }

      if (provider === "turkcell-efatura") {
        const res = await api.get(`/turkcell-efatura/inbox?page=${page}&page_size=${perPage}`);
        setShowingCachedData(res?.stale === true || res?.data_source === "cache");
        const rawItems = res?.items || res?.data || res?.result || res?.invoices || res?.list || (Array.isArray(res) ? res : []);
        const mapped = rawItems.map((inv) => ({
          id: inv.id || inv.ettn,
          invoice_no: inv.invoiceNumber || inv.invoice_number || "-",
          sender_name: inv.senderName || inv.senderTitle || inv.targetTitle || inv.sender_name || "-",
          sender_tax_no: inv.senderVkn || inv.targetVknTckn || inv.sender_tax_no || "-",
          issue_date: inv.issueDate || inv.issue_date || inv.executionDate || inv.executiondate || "",
          gross_total: inv.payableAmount ?? inv.gross_total ?? inv.totalAmount ?? inv.total_amount ?? null,
          total_amount: inv.payableAmount ?? inv.totalAmount ?? inv.total_amount ?? null,
          invoice_type: inv.type ?? inv.invoice_type,
          status: inv.statusText || inv.status_text || String(inv.status ?? ""),
          source: "turkcell",
        }));
        setItems(mapped);
        setTotal(res?.totalCount ?? res?.total ?? mapped.length);
        return;
      }

      const base = provider === "bizimhesap" ? "/bizimhesap/incoming-e-invoices" : "/parasut/incoming-e-invoices";
      const searchText = String(search || "").trim();
      const hasDeepLinkInvoice = Boolean(String(new URLSearchParams(location.search || "").get("invoice_id") || "").trim());
      const shouldUseWideFetch = searchText.length >= 2 || hasDeepLinkInvoice;

      if (!shouldUseWideFetch) {
        const data = await api.get(`${base}?page=${page}&per_page=${perPage}`);
        const rows = Array.isArray(data?.invoices) ? data.invoices : [];
        setItems(rows);
        setTotal(Number(data?.total || rows.length || 0));
        if (provider !== accountingProvider && allInvoices.length) {
          setAllInvoices([]);
        }
        return;
      }

      let source = allInvoices;
      if (provider !== accountingProvider && source.length) {
        source = [];
        setAllInvoices([]);
      }
      if (!source.length) {
        const cachedRows = readCache(provider);
        if (cachedRows?.length) {
          source = cachedRows;
        } else {
          source = await fetchAllInvoices(provider);
          writeCache(provider, source);
        }
        setAllInvoices(source);
      }
      applyPage(source, searchText, page);
    } catch (e) {
      toast.error(e.message || "Gelen e-faturalar yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [accountingProvider, allInvoices, applyPage, fetchAllInvoices, location.search, page, readCache, search, writeCache]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const stats = useMemo(() => {
    const rowsBase = allInvoices.length ? allInvoices : items;
    const searchedRows = String(search || "").trim()
      ? rowsBase.filter((x) =>
          [x.invoice_no, x.sender_name, x.uuid, x.status, x.scenario, x.profile_id, x.invoice_type]
            .map((v) => String(v || "").toLowerCase())
            .some((v) => v.includes(String(search || "").trim().toLowerCase()))
        )
      : rowsBase;
    const rows = searchedRows.filter((x) => isInPeriod(x?.issue_date, period));
    const officialized = rows.filter((x) => String(mapIncomingStatus(x.status)).toLowerCase().includes("resmile")).length;
    const draftSent = rows.filter((x) => String(mapIncomingStatus(x.status)).toLowerCase().includes("taslak")).length;
    const cancelled = rows.filter((x) => {
      const s = String(mapIncomingStatus(x.status)).toLowerCase();
      return s.includes("iptal");
    }).length;
    const gross = rows.reduce((acc, x) => acc + Number(x.gross_total || 0), 0);
    return { officialized, draftSent, cancelled, gross };
  }, [allInvoices, items, period, search]);

  const loadPdfResource = useCallback(async (invoiceId, opts = {}) => {
    const preferFileUrl = Boolean(opts?.preferFileUrl);
    if (accountingProvider !== "parasut" && accountingProvider !== "turkcell-efatura") {
      throw new Error("Bu muhasebe sağlayıcısında PDF görüntüleme henüz aktif değil");
    }
    const res = accountingProvider === "turkcell-efatura"
      ? await api.get(`/turkcell-efatura/inbox/${invoiceId}/pdf`)
      : await api.get(`/parasut/incoming-e-invoices/${invoiceId}/pdf`);
    if (preferFileUrl && res?.file_url) {
      return { url: res.file_url, revoke: false };
    }
    const b64 = res?.data_base64 || res?.data || "";
    if (!b64) throw new Error("PDF verisi alınamadı");
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: "application/pdf" });
    return { url: URL.createObjectURL(blob), revoke: true };
  }, [accountingProvider]);

  useEffect(() => {
    let cancelled = false;
    setQuickImportCapability(null);
    if (accountingProvider !== "turkcell-efatura") return undefined;
    api.get(`/incoming-invoices/quick-import/capability?provider=${encodeURIComponent(accountingProvider)}`, { silent_statuses: [404], no_retry: true })
      .then((res) => { if (!cancelled) setQuickImportCapability(res?.enabled === true && res?.preview === true ? res : null); })
      .catch(() => { if (!cancelled) setQuickImportCapability(null); });
    return () => { cancelled = true; };
  }, [accountingProvider]);

  const handlePreview = useCallback(async (row) => {
    try {
      setPreviewLoading(true);
      const fileRes = await loadPdfResource(row.id, { preferFileUrl: true });
      if (fileRes?.url && fileRes.revoke === false) {
        const opened = window.open(fileRes.url, "_blank", "noopener,noreferrer");
        if (opened) return;
      }
      const res = await loadPdfResource(row.id, { preferFileUrl: false });
      setPreviewTitle(row.invoice_no || row.uuid || "Gelen E-Fatura");
      setPreviewUrl(res.url);
      setPreviewOpen(true);
    } catch (e) {
      toast.error(e.message || "PDF görüntülenemedi");
    } finally {
      setPreviewLoading(false);
    }
  }, [loadPdfResource]);

  const handleDownload = useCallback(async (row) => {
    try {
      const res = await loadPdfResource(row.id, { preferFileUrl: true });
      const a = document.createElement("a");
      a.href = res.url;
      a.download = `${row.invoice_no || row.uuid || row.id || "gelen-e-fatura"}.pdf`;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
      if (res.revoke) setTimeout(() => URL.revokeObjectURL(res.url), 1500);
    } catch (e) {
      toast.error(e.message || "PDF indirilemedi");
    }
  }, [loadPdfResource]);

  useEffect(() => {
    let cancelled = false;
    setFlowInfo(null);
    if (!accountingProvider) return undefined;
    api.get(`/incoming-drafts/flow-enabled?provider=${encodeURIComponent(accountingProvider)}`)
      .then((res) => { if (!cancelled) setFlowInfo(res && typeof res === "object" ? res : null); })
      .catch(() => { if (!cancelled) setFlowInfo(null); });
    return () => { cancelled = true; };
  }, [accountingProvider]);

  const openStock = useCallback((row) => {
    setStockInvoice(row);
    const sourceLines = Array.isArray(row?.stock_items) ? row.stock_items : [];
    setStockLines(sourceLines.length ? sourceLines.map(line => ({
      name: line.name || "",
      sku: line.sku || "",
      barcode: line.barcode || "",
      quantity: Number(line.quantity || 1),
      unit_cost: Number(line.unit_cost || 0),
    })) : [{ name: "", sku: "", barcode: "", quantity: 1, unit_cost: 0 }]);
  }, []);

  const processStock = useCallback(async (e) => {
    e.preventDefault();
    const rows = stockLines.filter(x => String(x.name || "").trim() && Number(x.quantity) > 0);
    if (!rows.length || !stockInvoice?.id) { toast.error("En az bir geçerli ürün satırı ekleyin"); return; }
    setStockSaving(true);
    try {
      const stockProvider = accountingProvider;
      await api.post(`/finance/purchase-invoices/${stockProvider}/${stockInvoice.id}/process-stock`, {
        items: rows.map(x => ({ ...x, quantity: Number(x.quantity), unit_cost: Number(x.unit_cost || 0) })),
      });
      toast.success("Gelen fatura ürünleri stoğa işlendi");
      setStockInvoice(null);
    } catch (err) { toast.error(err?.message || "Stok işlemi tamamlanamadı"); }
    finally { setStockSaving(false); }
  }, [accountingProvider, stockInvoice, stockLines]);

  useEffect(() => {
    if (loading) return;
    const params = new URLSearchParams(location.search || "");
    const invoiceId = String(params.get("invoice_id") || "").trim();
    const action = String(params.get("action") || "view").trim().toLowerCase();
    if (!invoiceId) return;
    const key = `${invoiceId}:${action}`;
    if (deepLinkHandledRef.current === key) return;

    const source = (allInvoices && allInvoices.length ? allInvoices : items) || [];
    const target = source.find((x) => String(x?.id || "").trim() === invoiceId);
    if (!target) return;

    deepLinkHandledRef.current = key;
    if (action === "download") {
      handleDownload(target);
    } else {
      handlePreview(target);
    }
    if (typeof window !== "undefined") {
      const cleanUrl = `${window.location.pathname}`;
      window.history.replaceState({}, "", cleanUrl);
    }
  }, [location.search, loading, allInvoices, items, handleDownload, handlePreview]);

  useEffect(() => {
    if (previewOpen) return undefined;
    if (previewUrl && String(previewUrl).startsWith("blob:")) {
      URL.revokeObjectURL(previewUrl);
    }
    if (previewUrl) setPreviewUrl("");
    return undefined;
  }, [previewOpen, previewUrl]);

  if (notConnected) {
    return (
      <div className="page-shell min-h-[calc(100dvh-3.5rem)] overflow-x-hidden">
        <div className="surface-card p-6 text-sm text-muted-foreground">
          Bu ekran için bağlı muhasebe uygulaması bulunamadı.
        </div>
      </div>
    );
  }

  if (sourceRequired) {
    return (
      <div className="page-shell min-h-[calc(100dvh-3.5rem)]">
        <div className="surface-card p-6">
          <p className="text-sm font-semibold text-foreground">Gelen e-Fatura kaynağı seçilmeli</p>
          <p className="mt-1 text-sm text-muted-foreground">Birden fazla uygulama bağlı. Firmalarım sayfasından kullanılacak tek kaynağı seçin.</p>
        </div>
      </div>
    );
  }

  if (providerUnsupported) {
    const msg = "BizimHesap API dokümanında gelen e-fatura listeleme servisi bulunmuyor.";
    return (
      <div className="page-shell min-h-[calc(100dvh-3.5rem)]">
        <div className="surface-card p-6 text-sm text-muted-foreground">{msg}</div>
      </div>
    );
  }

  return (
    <div className="page-shell min-h-[calc(100dvh-3.5rem)] overflow-x-hidden">
      {osList ? (
        <Suspense fallback={null}>
          <IncomingInvoicesOS shell={{ ...buildFinanceShell(user, location.pathname, navVisibility), ...topbar }} onNavigate={(to) => navigate(to)}
            rows={allInvoices.length ? allInvoices : items} loading={loading}
            server={allInvoices.length ? undefined : { page, total, perPage, onPage: setPage }}
            notice={showingCachedData ? "Turkcell servisi geçici olarak yoğun. Son başarıyla alınan faturalar gösteriliyor." : null}
            sourceLabel={PROVIDER_LABELS[accountingProvider] || accountingProvider} canImport={quickImportEnabled} canPreview={accountingProvider !== "benimpos"}
            advancedLabel={flowEnabled ? "Gelişmiş işlem" : "Stoğa işle"} onAdvanced={(x) => (flowEnabled ? setFlowInvoice(x) : openStock(x))}
            onImport={(x) => setQuickInvoice(x)} onPreview={handlePreview} onDownload={handleDownload}
            renderDoc={(inv, o) => (
              <Suspense fallback={<p className="fui-page" role="status">Açılıyor…</p>}>
                <QuickImportWorkspace embedded open active={o.active} invoice={inv} onClose={o.onClose} />
              </Suspense>
            )} />
        </Suspense>
      ) : (
      <div className="surface-card overflow-hidden">
        <div className="sheet-heading flex-wrap gap-y-3">
          <div>
            <h2>Gelen E-Faturalar</h2>
            {!loading ? <p>{total} kayıt</p> : null}
          </div>
          {accountingProvider && (
            <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
              Kaynak: <span className="font-semibold text-foreground">{PROVIDER_LABELS[accountingProvider] || accountingProvider}</span>
            </div>
          )}
          <div className="sheet-heading-actions">
            <div className="flex items-center gap-3 rounded-lg border bg-background px-3 h-10 w-full max-w-sm">
              <Search className="h-4 w-4 text-muted-foreground shrink-0" />
              <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Ara..." className="flex-1 bg-transparent text-sm outline-none border-0 shadow-none h-auto px-0 focus-visible:ring-0" />
            </div>
          </div>
        </div>

        {showingCachedData ? (
          <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900" role="status">
            Turkcell servisi geçici olarak yoğun. Son başarıyla alınan faturalar gösteriliyor.
          </div>
        ) : null}

        {!loading && items.length > 0 ? (
          <div className="border-b border-border bg-card/60 p-3">
            <div className="mb-2 flex items-center justify-end gap-1">
              <Button variant={period === "7d" ? "default" : "outline"} size="sm" onClick={() => setPeriod("7d")} className="h-8 px-3 text-xs">7 Gün</Button>
              <Button variant={period === "30d" ? "default" : "outline"} size="sm" onClick={() => setPeriod("30d")} className="h-8 px-3 text-xs">30 Gün</Button>
              <Button variant={period === "all" ? "default" : "outline"} size="sm" onClick={() => setPeriod("all")} className="h-8 px-3 text-xs">Tümü</Button>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border border-emerald-200/70 bg-gradient-to-br from-emerald-50 to-white p-3 shadow-[0_8px_24px_rgba(16,185,129,0.08)]">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-emerald-700">Resmileşen</span>
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              </div>
              <div className="mt-2 text-xl font-semibold tabular-nums text-emerald-900">{stats.officialized}</div>
            </div>
            <div className="rounded-xl border border-amber-200/70 bg-gradient-to-br from-amber-50 to-white p-3 shadow-[0_8px_24px_rgba(245,158,11,0.08)]">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-amber-700">Taslak Gönderildi</span>
                <Clock3 className="h-4 w-4 text-amber-500" />
              </div>
              <div className="mt-2 text-xl font-semibold tabular-nums text-amber-900">{stats.draftSent}</div>
            </div>
            <div className="rounded-xl border border-rose-200/70 bg-gradient-to-br from-rose-50 to-white p-3 shadow-[0_8px_24px_rgba(244,63,94,0.08)]">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-rose-700">İptal</span>
                <CircleAlert className="h-4 w-4 text-rose-500" />
              </div>
              <div className="mt-2 text-xl font-semibold tabular-nums text-rose-900">{stats.cancelled}</div>
            </div>
            <div className="rounded-xl border border-sky-200/70 bg-gradient-to-br from-sky-50 to-white p-3 shadow-[0_8px_24px_rgba(14,165,233,0.08)]">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-sky-700">Toplam Tutar</span>
                <TrendingUp className="h-4 w-4 text-sky-500" />
              </div>
              <div className="mt-2 text-base font-semibold tabular-nums text-sky-900">{formatTryRight(stats.gross)}</div>
            </div>
          </div>
          </div>
        ) : null}

        <div className="p-3 md:p-4">
          {loading ? (
            <div className="h-56 flex items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Yükleniyor...
            </div>
          ) : items.length === 0 ? (
            <div className="h-56 flex items-center justify-center text-muted-foreground">
              <FileBadge2 className="h-5 w-5 mr-2" /> Kayıt bulunamadı
            </div>
          ) : (
            <>
              <div className="space-y-2 md:hidden">
                {items.map((x) => (
                  <div key={`card-${x.id}`} className="rounded-xl border border-border bg-card p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">{x.invoice_no || x.uuid || x.id || "-"}</div>
                        <div className="mt-0.5 text-xs text-muted-foreground truncate">{x.sender_name || x.supplier_name || "Belirtilmemiş"}</div>
                      </div>
                      <div className="shrink-0">{statusBadge(x.status)}</div>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                      <span>{formatDate(x.issue_date)}</span>
                      <span>{mapInvoiceTypeLabel(x.profile_id || x.invoice_type || x.scenario)}</span>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-sm font-semibold text-foreground">{formatTryRight(x.gross_total)}</span>
                      <div className="flex items-center gap-1">
                        {accountingProvider !== "benimpos" ? <>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handlePreview(x)} disabled={previewLoading}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDownload(x)} disabled={previewLoading}>
                            <Download className="h-4 w-4" />
                          </Button>
                        </> : null}
                        {!flowEnabled ? <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => openStock(x)}><PackagePlus className="h-4 w-4" /> Stoğa işle</Button> : null}
                        {quickImportEnabled ? <Button size="sm" className="h-8" onClick={() => setQuickInvoice(x)}>İçeri al</Button> : null}
                        {flowEnabled ? <Button variant="outline" size="sm" className="h-8" onClick={() => setFlowInvoice(x)}>{quickImportEnabled ? "Gelişmiş işlem" : "İşlem akışı"}</Button> : null}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="hidden overflow-auto rounded-lg border border-border md:block">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted/30">
                  <tr>
                    <th className="text-left px-3 py-3 w-[110px] text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Tarih</th>
                    <th className="text-left px-3 py-3 w-[170px] text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Fatura No</th>
                    <th className="text-left px-3 py-3 w-[320px] text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Gönderen</th>
                    <th className="text-left px-3 py-3 w-[100px] text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Fatura Tipi</th>
                    <th className="text-left px-3 py-3 w-[120px] text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Durum</th>
                    <th className="text-right px-3 py-3 w-[130px] text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Tutar</th>
                    <th className="text-right px-3 py-3 w-[190px] text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((x) => (
                    <tr key={x.id} className="border-b border-border/60">
                      <td className="px-3 py-2">{formatDate(x.issue_date)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{x.invoice_no || x.uuid || x.id || "-"}</td>
                      <td className="px-3 py-2">
                        <div className="line-clamp-2">{x.sender_name || x.supplier_name || "Belirtilmemiş"}</div>
                      </td>
                      <td className="px-3 py-2">{mapInvoiceTypeLabel(x.profile_id || x.invoice_type || x.scenario)}</td>
                      <td className="px-3 py-2">{statusBadge(x.status)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">{formatTryRight(x.gross_total)}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-1">
                          {accountingProvider !== "benimpos" ? <>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handlePreview(x)} disabled={previewLoading}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDownload(x)} disabled={previewLoading}>
                              <Download className="h-4 w-4" />
                            </Button>
                          </> : null}
                          {!flowEnabled ? <Button variant="outline" size="sm" className="h-8 gap-1" onClick={() => openStock(x)}><PackagePlus className="h-3.5 w-3.5" /> Stoğa işle</Button> : null}
                          {quickImportEnabled ? <Button size="sm" className="h-8" onClick={() => setQuickInvoice(x)}>İçeri al</Button> : null}
                          {flowEnabled ? <Button variant="outline" size="sm" className="h-8" onClick={() => setFlowInvoice(x)}>{quickImportEnabled ? "Gelişmiş işlem" : "İşlem akışı"}</Button> : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-1 border-t border-border px-4 py-3">
          <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
            let p;
            if (totalPages <= 5) p = i + 1;
            else if (page <= 3) p = i + 1;
            else if (page >= totalPages - 2) p = totalPages - 4 + i;
            else p = page - 2 + i;
            return (
              <Button
                key={p}
                variant={page === p ? "default" : "outline"}
                size="icon"
                onClick={() => setPage(p)}
                className="h-8 w-8 text-xs font-medium"
              >
                {p}
              </Button>
            );
          })}
          <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
      )}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>{previewTitle}</DialogTitle>
          </DialogHeader>
          <div className="h-[75vh] rounded-md border border-border overflow-hidden">
            {previewUrl ? (
              <iframe title="incoming-e-invoice-pdf" src={previewUrl} className="w-full h-full" />
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">PDF yüklenemedi</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {flowEnabled ? (
        <IncomingInvoiceFlowDialog
          open={Boolean(flowInvoice)} onClose={() => setFlowInvoice(null)}
          invoice={flowInvoice} provider={accountingProvider} inventoryOwner={flowInfo?.inventory_owner || null}
          capabilities={flowInfo}
        />
      ) : null}
      {/* Üç adımlı pencere yalnız sunucu `wizard: true` dediğinde (tenant allowlist); aksi hâlde eski pencere aynen. Wizard salt okunurdur, onay göndermez. */}
      {/* Tek ekranlı çalışma alanı (finans kiti) yalnız sunucu hem `wizard` hem `workspace` true dediğinde; aksi hâlde üç adımlı pencere. */}
      {/* OS kipinde İçeri Al belge sekmesi olarak açılır (renderDoc); burada yalnız kodu önceden yüklemek için kapalı bileşen durur. */}
      {osList ? (
        <Suspense fallback={null}><QuickImportWorkspace open={false} onClose={() => {}} invoice={null} /></Suspense>
      ) : null}
      {quickImportEnabled && quickImportCapability?.wizard === true && quickImportCapability?.workspace !== true ? (
        <QuickImportWizard open={Boolean(quickInvoice)} onClose={() => setQuickInvoice(null)} invoice={quickInvoice}
                           warehouseManagement={quickImportCapability?.warehouse_management === true} />
      ) : null}
      {quickImportEnabled && quickImportCapability?.wizard !== true ? (
        <QuickImportDialog open={Boolean(quickInvoice)} onClose={() => setQuickInvoice(null)} invoice={quickInvoice}
                           confirmEnabled={quickImportCapability?.confirm === true}
                           onCompleted={() => { setQuickInvoice(null); fetchData(); }} />
      ) : null}
      <Dialog open={Boolean(stockInvoice)} onOpenChange={(open) => { if (!open) setStockInvoice(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Gelen faturayı stoğa işle</DialogTitle></DialogHeader>
          <form onSubmit={processStock} className="space-y-4">
            <div className="rounded-xl border border-cyan-200 bg-cyan-50/70 p-3 text-sm text-cyan-900">
              <strong>{stockInvoice?.invoice_no || "Gelen fatura"}</strong> · {stockInvoice?.sender_name || "Tedarikçi"}<br />
              <span className="text-xs text-cyan-700">Ürünleri mevcut SKU ile eşleştirin; bulunamayanlar ObiHub'da yeni ürün olarak oluşturulur. Bu işlem aynı fatura için yalnızca bir kez yapılabilir.</span>
            </div>
            <div className="space-y-2">
              {stockLines.map((line, i) => (
                <div key={i} className="grid grid-cols-[1fr_140px_90px_120px_36px] gap-2 rounded-xl border border-border p-2">
                  <Input required placeholder="Ürün adı" value={line.name} onChange={e => setStockLines(rows => rows.map((r,j) => j===i ? {...r,name:e.target.value}:r))} />
                  <Input placeholder="SKU / Stok kodu" value={line.sku} onChange={e => setStockLines(rows => rows.map((r,j) => j===i ? {...r,sku:e.target.value}:r))} />
                  <Input required type="number" min="1" placeholder="Adet" value={line.quantity} onChange={e => setStockLines(rows => rows.map((r,j) => j===i ? {...r,quantity:e.target.value}:r))} />
                  <Input type="number" min="0" step="0.01" placeholder="Birim maliyet" value={line.unit_cost} onChange={e => setStockLines(rows => rows.map((r,j) => j===i ? {...r,unit_cost:e.target.value}:r))} />
                  <Button type="button" variant="ghost" size="icon" disabled={stockLines.length===1} onClick={() => setStockLines(rows => rows.filter((_,j) => j!==i))}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => setStockLines(rows => [...rows,{name:"",sku:"",quantity:1,unit_cost:0}])}><Plus className="mr-1.5 h-4 w-4" /> Ürün satırı ekle</Button>
            <div className="flex justify-end gap-2 border-t border-border pt-4"><Button type="button" variant="outline" onClick={() => setStockInvoice(null)}>Vazgeç</Button><Button type="submit" disabled={stockSaving}>{stockSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Stoğu artır</Button></div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
